import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { FormGroup, Validators } from '@angular/forms';
import { IFormKey } from 'src/app/modules/shared/models/forms';
import { LoggerService } from 'src/app/services/logger.service';
import { CommonUtils } from 'src/app/utils/CommonUtils';
import { contractor_keys_PF, contractor_keys_PG, legal_guardian_form_keys, legal_guardian_form_template, legal_guardian_form_validators, search_form_keys } from './contractor-change-isv.constants';
import { ESpecificBeneficiaryType } from '../beneficiary-form-isv/beneficiary-form-isv.constants';
import { Subject, take } from 'rxjs';
import { IObjectKey } from 'src/app/modules/shared/models/objects';
import { RegistrySearchIsvComponent } from '../registry-search-isv/registry-search-isv.component';
import { MatDialog } from '@angular/material/dialog';

@Component({
  selector: 'app-contractor-change-isv',
  templateUrl: './contractor-change-isv.component.html',
  styleUrl: './contractor-change-isv.component.scss'
})
export class ContractorChangeIsvComponent implements OnInit, OnDestroy{

  @Input() searchForm: FormGroup;
  @Input() isReadOnly: boolean;
  @Input() searchedSubject: any;
  @Input() searchedLegalGuardian: any;
  @Input() searchCompleted: boolean;
  @Input() legalGuardianForm: FormGroup;
  
  @Output() onContractorSearch = new EventEmitter<string>();
  @Output() onContractorReset = new EventEmitter<void>();
  @Output() onLegalGuardianSearch = new EventEmitter<any>();
  
  searchFormKeys: IFormKey[] = CommonUtils.assign(search_form_keys);
  legalGuardianFormKeys: IFormKey[] = CommonUtils.assign(legal_guardian_form_keys);
  type: string;

  destroy$: Subject<void> = new Subject<void>();

  constructor(
    private readonly _dialog: MatDialog,
  ) { }

  ngOnInit(): void {
    if (this.isReadOnly) {
      this.searchForm.disable();
    } else {
      this.manageSearchForm();
    }
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  manageSearchForm(): void {
    if (this.searchForm) {
      this.searchForm.get('type').valueChanges.subscribe(value => {
        this.onContractorReset.emit();
        this.searchForm.get('taxCode').setValue(null);
        const isPersonaFisica = value === ESpecificBeneficiaryType.PERSONA_FISICA;
        const validators = isPersonaFisica 
          ? [Validators.required, Validators.pattern(/^[A-Z]{6}[0-9]{2}[A-Z][0-9]{2}[A-Z][A-Z0-9]{4}$/)]
          : [Validators.required];
        this.searchForm.get('taxCode').setValidators(validators);
      });
    }
  }

  onSearchClick(): void {
    LoggerService.logInfo(this.constructor.name, 'onSearchClick', this.searchForm.value);
    this.onContractorSearch.emit(this.searchForm?.get('taxCode')?.value);
  }

  reset(): void {
    LoggerService.logInfo(this.constructor.name, 'reset');
    this.searchForm.reset();
    this.onContractorReset.emit();
  }

  openLegalGuardianSearchModal(): void {
    LoggerService.logInfo(this.constructor.name, 'openLegalGuardianSearchModal');
    this._dialog.open(RegistrySearchIsvComponent, { disableClose: true })
      .afterClosed()
      .pipe(take(1))
      .subscribe((outcome) => {
        LoggerService.logInfo(this.constructor.name, 'openLegalGuardianSearchModal', outcome);
        if (outcome) {
          
          this.onLegalGuardianSearch.emit(outcome);
        }
      });  
  }

  get subjectName(): string {
    if (this.searchedSubject) {
      return 'firstName' in this.searchedSubject
        ? `${this.searchedSubject.firstName} ${this.searchedSubject.lastName}`
        : this.searchedSubject.companyName;
    }
  }

  get subjectKeys(): IObjectKey[] {
    if (this.searchedSubject) {
      return 'firstName' in this.searchedSubject
        ? contractor_keys_PF
        : contractor_keys_PG;
    }
  }

  get legalGuardianName(): string {
    if (this.searchedLegalGuardian) {
      return `${this.searchedLegalGuardian.firstName} ${this.searchedLegalGuardian.lastName}`
    }
  }

  get legalGuardianKeys(): IObjectKey[] {
    if (this.searchedLegalGuardian) {
      return contractor_keys_PF;
    }
  }

  get isSearchedSubjectPF(): boolean {
    return this.searchForm?.get('type')?.value === ESpecificBeneficiaryType.PERSONA_FISICA;
  }

  get isSearchedSubjectPG(): boolean {
    return this.searchForm?.get('type')?.value === ESpecificBeneficiaryType.PERSONA_GIURIDICA;
  }

  get canAddLegalGuardian(): boolean {
    return this.isSearchedSubjectPG || (this.isSearchedSubjectPF && this.legalGuardianForm?.get('isContractorCapable')?.value === 'NO');
  }

}
